import React, { useCallback, useState } from 'react';
import styled, { css } from 'styled-components';

import { __dev__ } from 'utils';
import { Flex } from 'components/layouts';
import { BlueButton as Button } from 'components/shared';

import { useImageNavigation } from '../hooks/useImageNavigation';
import { ImageFile } from 'redux/images/images.type';
import { LabellingMode, Image } from 'redux/annotations/annotations.type';
import { useCanEditCurrentImage, useCanEditBlurRegions } from '../hooks/useCanEditCurrentImage';
import { FinishConfirmationModal } from './finish-confirmation-modal';

const DISPLAY_NAME = 'ImageDetailFooter';

const Container = styled(Flex)`
  width: 100%;
  justify-content: space-between;
  align-items: center;
  height: 64px;
`;

const nextAndPreviousCss = css`
  min-width: 200px;
  max-width: 200px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
`;

const svgProps = {
  width: 16,
  height: 16,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
};

const ChevronLeftIcon = () => (
  <svg {...svgProps}><polyline points="15 18 9 12 15 6" /></svg>
);

const ChevronRightIcon = () => (
  <svg {...svgProps}><polyline points="9 18 15 12 9 6" /></svg>
);

const SaveIcon = () => (
  <svg {...svgProps}>
    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
    <polyline points="17 21 17 13 7 13 7 21" />
    <polyline points="7 3 7 8 15 8" />
  </svg>
);

const CheckIcon = () => (
  <svg {...svgProps}><polyline points="20 6 9 17 4 12" /></svg>
);
const Next = styled(Button)`
  ${nextAndPreviousCss}
`;

const Previous = styled(Button)`
  ${nextAndPreviousCss}
`;

const Finish = styled(Button)`
  background: #23a942;
  ${nextAndPreviousCss}
`;

const Save = styled(Button)`
  background: #23a942;
  ${nextAndPreviousCss}
`;

// Three equal-width slots keep Previous left, Save centered and Next/Finish right,
// regardless of which of the buttons are currently visible.
const Slot = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
`;

const LeftSlot = styled(Slot)`
  justify-content: flex-start;
`;

const CenterSlot = styled(Slot)`
  justify-content: center;
`;

const RightSlot = styled(Slot)`
  justify-content: flex-end;
`;

type PrimitiveDivProps = React.ComponentPropsWithoutRef<'div'>;
type ImageDetailFooterElement = React.ElementRef<'div'>;

interface ImageDetailFooterProps extends PrimitiveDivProps {
  photoSeriesId: string | undefined,
  imageId: string | undefined,
  labelStudioRef: React.MutableRefObject<any> | null,
  imageFile: ImageFile,
  handleSubmitAnnotation: any,
  labellingMode: LabellingMode,
  imagesForLabelling: Image[] | undefined,
}

const useSaveImage = (
  labelStudioRef: any,
  imageFile: ImageFile,
  handleSubmitAnnotation: (
    areas:any,
    imageFile: ImageFile,
    confirm: boolean | undefined,
    onConfirm: () => void,
    onSave: () => void,
    labelStudioRef: any
  ) => void
) => {
  const { navigateToHome } = useImageNavigation();

  return useCallback((confirm: boolean = false, onSave: () => void = () => {}) => {
    if (labelStudioRef == null) return;

    const areas = labelStudioRef.current.store.annotationStore.selected.areas;
    handleSubmitAnnotation(areas, imageFile, confirm, navigateToHome, onSave, labelStudioRef);
  }, [handleSubmitAnnotation, imageFile, labelStudioRef, navigateToHome]);
};

const ImageDetailFooter = React.forwardRef<ImageDetailFooterElement, ImageDetailFooterProps>((
  { labelStudioRef, imageFile, handleSubmitAnnotation, photoSeriesId, labellingMode, imageId, imagesForLabelling, ...props },
  forwardedRef
) => {
  const { hasImages, isFirstImage, isLastImage, navigateNext, navigatePrev } = useImageNavigation();
  const saveImage = useSaveImage(labelStudioRef, imageFile, handleSubmitAnnotation);
  const canEditPhotoSeries = useCanEditCurrentImage(photoSeriesId, imageId, labellingMode);
  const canEditBlurRegions = useCanEditBlurRegions(photoSeriesId, imageId, labellingMode);
  const [showFinishConfirm, setShowFinishConfirm] = useState(false);

  if (photoSeriesId === undefined) return null;

  // Saving is allowed either when the image is fully editable, or when only blur regions are
  // editable (windshield repair/replace review). Confirming (Finish) stays fully locked in that case.
  const canSavePhotoSeries = canEditPhotoSeries() || canEditBlurRegions();
  const canConfirmPhotoSeries = canEditPhotoSeries();
  // In windshield repair/replace review, Next/Previous must not auto-save; only the Save button saves.
  const canAutoSaveOnNavigate = canEditPhotoSeries();
  const canFinish = isLastImage() &&
    (labellingMode === LabellingMode.DamageDetection || labellingMode === LabellingMode.LabelReview || labellingMode === LabellingMode.ModelTraining);

  return (
    <Container {...props} ref={forwardedRef}>
      <LeftSlot>
        {
          hasImages && (
            <Previous
              style={{ visibility: isFirstImage() ? 'hidden' : 'visible' }}
              onClick={() => {
                if (canAutoSaveOnNavigate) saveImage(false, () => navigatePrev());
                else navigatePrev();
              }}
              data-testid="previousBtn"
            >
              <ChevronLeftIcon />
              <span>Previous</span>
            </Previous>
          )
        }
      </LeftSlot>
      <CenterSlot>
        { (canSavePhotoSeries) && (
          <Save onClick={() => saveImage()} data-testid="saveBtn">
            <SaveIcon />
            <span>Save</span>
          </Save>
        ) }
      </CenterSlot>
      <RightSlot>
        {
          hasImages && (
            canFinish ? (
              canConfirmPhotoSeries && (
                <Finish
                  onClick={() => setShowFinishConfirm(true)}
                  data-testid="finishBtn"
                >
                  <CheckIcon />
                  <span>Finish</span>
                </Finish>
              )
            ) : !isLastImage() ? (
              <Next
                onClick={() => {
                  if (canAutoSaveOnNavigate) saveImage(false, () => navigateNext());
                  else navigateNext();
                }}
                data-testid="nextBtn"
              >
                <span>Next</span>
                <ChevronRightIcon />
              </Next>
            ) : null
          )
        }
      </RightSlot>
      <FinishConfirmationModal
        show={showFinishConfirm}
        onHide={() => setShowFinishConfirm(false)}
        imagesForLabelling={imagesForLabelling}
        onConfirm={() => saveImage(true)}
        photoSeriesId={photoSeriesId}
        labellingMode={labellingMode}
      />
    </Container>
  );
});

if (__dev__) {
  ImageDetailFooter.displayName = DISPLAY_NAME;
}

export { ImageDetailFooter };
